import { Component, Vue } from "vue-property-decorator";
import { getApplicationDetails } from "@/providers/apis/getApplictionDetails";
import { utils } from "@/utils/utils";
import moment from "moment";
import AppLoaderComponent from "../app-loader/app-loader.component.vue";
import PreviewPopupComponent from "../preview-popup/preview-popup.component.vue";
import { appConfigService } from "@/providers/services/app/app-config";
import { ReviewerpeerPreviewInterface } from "@/models/reviewer-peer-applications/reviewer-peers-applications.interface";
import EmailModalPopupComponent from "../email-modal-popup/email-modal-popup.component.vue";
import RevokeCertificatePopupComponent from "../revoke-certificate-popup/revoke-certificate-popup.component.vue";
import { PEER_FLAG_APPLICANT_QUERY } from "@/graphql/peer-flag-applicant";
import { gql } from "graphql-tag";
import { flagApplicantApi } from "@/providers/apis/peers-flag-applicant";
import { trainingFormApi } from "@/providers/apis/training-form";
import { certificationFormApi } from "@/providers/apis/certification-form";
import { renewalFormApi } from "@/providers/apis/renewal-application";
import { adminUploadsApi } from "@/providers/apis/admin-uploads";
import { commonService } from "@/providers/services/common-service";
@Component({
  name: "reviewer-peers-preview",
  components: {
    AppLoaderComponent,
    PreviewPopupComponent,
    EmailModalPopupComponent,
    RevokeCertificatePopupComponent,
  },
})
export default class ReviewerPeersPreview extends Vue {
  public isLoading = false;
  public errorMessage = "";
  public profileData: any = [];
  public certificationData: any = [];

  /**
   * Whether a certificate number exists to act on. Revoke used to be offered on
   * the strength of the row alone, so it sent certificateNumber null and the
   * API answered 400.
   */
  public get hasIssuedCertificateNumber(): boolean {
    const certificate = this.certificationData && this.certificationData[0];
    return !!(
      certificate && String(certificate.certificateNumber || "").trim()
    );
  }

  /**
   * Compared by date alone, never by time of day. Only a date later than today
   * counts as live: revoking stamps the certificate's expiry with the day it
   * was revoked, so an expiry of today is the mark of a certificate that has
   * just been revoked rather than one still running.
   */
  public get certificateIsActive(): boolean {
    const certificate = this.certificationData && this.certificationData[0];
    if (!certificate || !certificate.expiryDate) {
      return false;
    }
    const expiry = new Date(certificate.expiryDate);
    if (isNaN(expiry.getTime())) {
      return false;
    }
    expiry.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return expiry > today;
  }

  /**
   * Which of the two actions the control offers. A peer flagged as revoked who
   * holds a live, numbered certificate has been certified again since, so the
   * useful action is to revoke that one. Every other revoked case — expired,
   * unnumbered, missing — leaves the revoked flag standing alone, and undoing
   * it is the only thing left to do.
   */
  public get isUnrevokeAction(): boolean {
    if (!this.peerRevokeUnRevoke) {
      return false;
    }
    return !(this.certificateIsActive && this.hasIssuedCertificateNumber);
  }

  /**
   * Whether the control belongs on the page at all. A revoked peer always keeps
   * one, so the revocation can always be undone — offering nothing would strand
   * them revoked. Revoking needs a certificate that has actually been issued
   * and is still live; with nothing to revoke the button is left out rather
   * than shown dead.
   */
  public get showRevokeButton(): boolean {
    if (this.peerRevokeUnRevoke) {
      return true;
    }
    return (
      this.hasIssuedCertificateNumber &&
      this.certificationData.length > 0 &&
      this.certificateIsActive
    );
  }
  public file: any = null;
  public showPreviewPopup = false;
  public tableHeader = [
    { key: "applicationTitle", label: "Application Name" },
    { key: "submittedAt", label: "Submission Date" },
    { key: "progressStatus", label: "Status" },
    { key: "action", label: "Action" },
  ];
  public applicationData = [];
  public imgType = ["jpg", "png", "jpeg", "tiff"];
  public getCertificatesFilesPathUrl = "";
  public previewData: any[] = [];
  public emailData: any = null;
  public flagApplicantGrapqlRevoke: any = {};
  public flagHistoryDataApi: any = {};
  public disablePeerProfileEnable = false;
  public peerApplicationDisable: any = null;
  public thumnailData = "";
  public S3Path = "";
  public isShowImpersonate: any;
  public revokeUnrevokeCertificate = false;
  public peerRevokeUnRevoke: any = null;
  public showDelete = appConfigService.getShowDelete();
  public deleteTargetId = "";
  public deleteApplicationName = "";
  public deleteApplicationTitle = "";
  public deleteFirstName = "";
  public deleteLastName = "";
  public deleteSubmittedAt = "";
  public deleteProgressStatus = "";
  public deleteSuccess = false;
  // Admin Uploads tab
  public activeTab = "applications";
  public adminDocuments: any[] = [];
  public adminDocumentsLoading = false;
  public adminUploadForm: any = {
    documentName: "",
    description: "",
    visibleToPeer: false,
  };
  public selectedFile: any = null;
  public uploadInProgress = false;
  public uploadError = "";
  // certificate previews resolve against the certificates S3 path; admin
  // uploaded documents resolve against the general files S3 path.
  public previewIsPeer = true;
  public deleteDocumentTarget: any = null;
  public imageExtensions = ["jpg", "jpeg", "png", "tiff"];
  public created() {
    const id: any = this.$route.query.id;
    this.getCertificatesFilesPathUrl =
      appConfigService.getS3CertificatesFilesPathUrl();
    this.getPeerApplicationLists(id);
    this.flagApplicantGrapqlQuery();
    this.flagHistoryApi();
    this.S3Path = appConfigService.getS3FilesPathUrl();
  }

  public getPeerApplicationLists(id: string) {
    this.isLoading = true;
    getApplicationDetails
      .getPeersPreviewApplicationList({ id: id })
      .then((response) => {
        this.thumnailData = response.thumbnail;
        this.peerApplicationDisable = response.isDisabled;
        this.peerRevokeUnRevoke = response.isRevoked;
        this.applicationData = response.appplications.sort((a: any, b: any) => {
          return (
            new Date(b.submittedAt).getTime() -
            new Date(a.submittedAt).getTime()
          );
        });
        this.isLoading = false;
        this.profileData = this.applicationData.find(
          (data: ReviewerpeerPreviewInterface) => {
            return data.applicationName === "PART ONE";
          }
        );
        this.certificationData = this.applicationData.filter(
          (data: ReviewerpeerPreviewInterface) => {
            data.certificateFile = JSON.parse(data.certificateFile);
            if (
              data &&
              data.progressStatus === "ACCEPTED" &&
              data.applicationName === "PART TWO"
            ) {
              return data;
            }
          }
        );
        this.applicationData.forEach((data: ReviewerpeerPreviewInterface) => {
          if (data.applicationName === "PART ONE") {
            data.applicationTitle = "Training Application";
          }
          if (data.applicationName === "PART TWO") {
            data.applicationTitle = "Certification Application";
          }
          if (data.applicationName === "RENEWAL") {
            data.applicationTitle = "Renewal Application";
          }
          data.submittedAt = data.submittedAt
            ? moment(data.submittedAt).format("MM-DD-YYYY")
            : "--";
          data.expiryDate = data.expiryDate
            ? moment(data.expiryDate).format("MM-DD-YYYY")
            : "--";
        });
        this.isShowImpersonate = this.applicationData.filter(
          (application: any) => {
            return (
              application &&
              application.progressStatus === "ACCEPTED" &&
              application.applicationName === "PART ONE"
            );
          }
        );
        // profileData (and its email) are now available — load admin documents.
        this.fetchAdminDocuments();
      })
      .catch((error) => {
        this.errorMessage = utils.errorMessage(error);
        this.isLoading = false;
      });
  }
  public backUrl() {
    let filter = "";
    if (this.$route.query.trainingcity) {
      filter += `&trainingcity=${this.$route.query.trainingcity}`;
    }

    if (this.$route.query.region) {
      filter += `&region=${this.$route.query.region}&regionId=${this.$route.query.regionId}`;
    }
    if (this.$route.query.age) {
      filter += `&age=${this.$route.query.age}&fromAge=${this.$route.query.fromAge}&toAge=${this.$route.query.toAge}`;
    }
    if (this.$route.query.education) {
      filter += `&education=${this.$route.query.education}`;
    }
    if (this.$route.query.gender) {
      filter += `&gender=${this.$route.query.gender}`;
    }

    return this.$route.query.isReport
      ? `/reviewer-dashboard/peers?type=${this.$route.query.type}&isReport=${this.$route.query.isReport}&fromDate=${this.$route.query.fromDate}&toDate=${this.$route.query.toDate}${filter}`
      : "/reviewer-dashboard/peers";
  }
  public previewForm(row: any) {
    let query;
    if (this.$route.query.isReport) {
      query = {
        id: this.$route.query.id,
        application: row.item.applicationName,
        type: this.$route.query.type,
        isReport: this.$route.query.isReport,
        fromDate: this.$route.query.fromDate,
        toDate: this.$route.query.toDate,
      };
      if (this.$route.query.trainingcity) {
        Object.assign(query, { trainingcity: this.$route.query.trainingcity });
      }
      if (this.$route.query.gender) {
        Object.assign(query, { gender: this.$route.query.gender });
      }
      if (this.$route.query.region) {
        Object.assign(query, { region: this.$route.query.region });
        Object.assign(query, { regionId: this.$route.query.regionId });
      }
      if (this.$route.query.age) {
        Object.assign(query, { age: this.$route.query.age });
        Object.assign(query, { fromAge: this.$route.query.fromAge });
        Object.assign(query, { toAge: this.$route.query.toAge });
      }
      if (this.$route.query.education) {
        Object.assign(query, { education: this.$route.query.education });
      }
    } else {
      query = {
        id: this.$route.query.id,
        application: row.item.applicationName,
      };
    }
    this.$router.push({
      path: `peers-preview/${row.item.id}`,
      query: query,
    });
  }
  public previewCertificationForm(content: any) {
    this.previewIsPeer = true;
    this.file = content.certificateFile;
    if (this.file) {
      const img = new Image();
      this.previewData = [
        {
          width: img.width,
          height: img.height,
          type: this.file.type,
          originalFileName: this.file.originalFileName,
          uploadedFileName: this.file.uploadedFileName,
        },
      ];
      this.showPreviewPopup = true;
    }
  }
  public hidePreviewPopup(event: boolean) {
    if (event === true) {
      this.showPreviewPopup = false;
      this.previewData = [];
    }
  }
  public onOpenModal(row: any) {
    this.emailData = row;
    this.$bvModal.show("email-modal");
  }
  public emailSentStatus(event: boolean) {
    if (event) {
      this.$bvModal.hide("email-modal");
    }
  }
  public revokeCertification() {
    this.revokeUnrevokeCertificate = true;
    this.disablePeerProfileEnable = false;
    this.$bvModal.show("revoke-certificate-modal");
  }
  public RevokeCertificatePopupClose() {
    this.$bvModal.hide("revoke-certificate-modal");
    const id: any = this.$route.query.id;
    this.getPeerApplicationLists(id);
    this.flagHistoryApi();
  }
  public flagHistoryApi() {
    flagApplicantApi
      .peersFlagHistory({ id: this.$route.query.id })
      .then((res) => {
        this.flagHistoryDataApi = res;
      });
  }
  public disablePeerProfile() {
    this.disablePeerProfileEnable = true;
    this.revokeUnrevokeCertificate = false;
    this.$bvModal.show("revoke-certificate-modal");
  }
  public flagApplicantGrapqlQuery() {
    this.$apollo
      .query({
        query: gql`query ${PEER_FLAG_APPLICANT_QUERY}`,
      })
      .then((result) => {
        this.flagApplicantGrapqlRevoke = result;
      });
  }
  public deleteProfilePic() {
    this.$bvModal.show("profile-pic-delete");
  }
  public cancelProfilePic() {
    this.$bvModal.hide("profile-pic-delete");
  }
  public submitProfilePic() {
    this.isLoading = true;
    getApplicationDetails
      .getPeersProfilePicDelete({ id: this.$route.query.id })
      .then((res) => {
        if (res.status == 204) {
          const id: any = this.$route.query.id;
          this.$bvModal.hide("profile-pic-delete");
          this.getPeerApplicationLists(id);
        }
      });
  }
  public confirmDelete(row: any) {
    this.deleteTargetId = row.item.id;
    this.deleteApplicationName = row.item.applicationName;
    this.deleteApplicationTitle = row.item.applicationTitle || "";
    this.deleteFirstName =
      (this.profileData as any)?.data?.certificationPartOneBasicDetails
        ?.firstName || "";
    this.deleteLastName =
      (this.profileData as any)?.data?.certificationPartOneBasicDetails
        ?.lastName || "";
    this.deleteSubmittedAt = row.item.submittedAt || "--";
    this.deleteProgressStatus = row.item.progressStatus || "";
    this.deleteSuccess = false;
    this.$bvModal.show("delete-application-modal-peers");
  }
  public cancelDelete() {
    this.deleteSuccess = false;
    this.$bvModal.hide("delete-application-modal-peers");
  }
  public submitDelete() {
    this.isLoading = true;
    let deletePromise;
    if (this.deleteApplicationName === "PART ONE") {
      deletePromise = trainingFormApi.deleteTrainingApplication(
        this.deleteTargetId
      );
    } else if (this.deleteApplicationName === "PART TWO") {
      deletePromise = certificationFormApi.deleteCertificateApplication(
        this.deleteTargetId
      );
    } else {
      deletePromise = renewalFormApi.deleteRenewalApplication(
        this.deleteTargetId
      );
    }
    deletePromise
      .then(() => {
        this.isLoading = false;
        this.deleteSuccess = true;
        const id: any = this.$route.query.id;
        this.getPeerApplicationLists(id);
        setTimeout(() => {
          this.$bvModal.hide("delete-application-modal-peers");
        }, 3000);
      })
      .catch((error: any) => {
        this.errorMessage = utils.errorMessage(error);
        this.isLoading = false;
      });
  }
  public impersonateUser() {
    const id: any = this.$route.query.id;
    getApplicationDetails.getImpersonate({ id: id }).then((response) => {
      const accessToken: any = localStorage.getItem("access_token");
      const userDetail: any = localStorage.getItem("user_details");
      localStorage.setItem("admin_access_token", accessToken);
      localStorage.setItem("admin_user_details", userDetail);
      localStorage.setItem("access_token", response.access_token);
      localStorage.setItem("user_details", JSON.stringify(response));
      localStorage.setItem("profileData", userDetail);
      this.$router.push({
        path: `/user-dashboard/home`,
        query: {
          id: id,
          type: "admin",
        },
      });
      localStorage.setItem("impersonateId", id);
      window.location.reload();
    });
  }

  // ------------------------- Admin Uploads -------------------------

  public switchTab(tab: string) {
    this.activeTab = tab;
  }

  public fetchAdminDocuments() {
    // Admin documents are keyed by the peer's email, which is only available
    // once the peer's applications have loaded.
    const email: any = (this.profileData as any)?.data
      ?.certificationPartOneBasicDetails?.email;
    if (!email) {
      return;
    }
    this.adminDocumentsLoading = true;
    adminUploadsApi
      .getPeerDocuments(email)
      .then((response: any) => {
        this.adminDocuments = (response && response.documents) || [];
        this.adminDocumentsLoading = false;
      })
      .catch((error) => {
        this.errorMessage = utils.errorMessage(error);
        this.adminDocumentsLoading = false;
      });
  }

  public openUploadModal() {
    this.adminUploadForm = {
      documentName: "",
      description: "",
      visibleToPeer: false,
    };
    this.selectedFile = null;
    this.uploadError = "";
    this.$bvModal.show("admin-upload-modal");
  }

  public closeUploadModal() {
    this.$bvModal.hide("admin-upload-modal");
  }

  public onFileSelect(event: any) {
    const files = event.target.files || event.dataTransfer.files;
    if (files && files.length) {
      this.selectedFile = files[0];
      this.uploadError = "";
    }
  }

  public onFileDrop(event: any) {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files && files.length) {
      this.selectedFile = files[0];
      this.uploadError = "";
    }
  }

  public removeSelectedFile() {
    this.selectedFile = null;
  }

  public submitUpload() {
    if (!this.adminUploadForm.documentName) {
      this.uploadError = "Please enter a document name";
      return;
    }
    if (!this.selectedFile) {
      this.uploadError = "Please attach a document";
      return;
    }
    this.uploadInProgress = true;
    this.uploadError = "";
    commonService
      .uploadFile([this.selectedFile])
      .then((result: any) => {
        const fileBlock = (result && result.data) || [];
        const uploaded = fileBlock.find((data: any) => !(data instanceof File));
        if (!uploaded || !uploaded.fileName) {
          this.uploadInProgress = false;
          this.uploadError = "File upload failed, please try again";
          return;
        }
        const payload = {
          userId: this.$route.query.id,
          email: (this.profileData as any)?.data
            ?.certificationPartOneBasicDetails?.email,
          documentName: this.adminUploadForm.documentName,
          description: this.adminUploadForm.description,
          fileName: uploaded.fileName,
          originalFileName: uploaded.originalFileName || this.selectedFile.name,
          contentType: this.selectedFile.type,
          visibleToPeer: this.adminUploadForm.visibleToPeer,
        };
        return adminUploadsApi.createPeerDocument(payload).then(() => {
          this.uploadInProgress = false;
          this.closeUploadModal();
          this.fetchAdminDocuments();
        });
      })
      .catch((error) => {
        this.uploadInProgress = false;
        this.uploadError = utils.errorMessage(error);
      });
  }

  public toggleVisibility(document: any) {
    const updatedVisibility = !document.visible_to_peer;
    adminUploadsApi
      .updateDocumentVisibility(document.id, updatedVisibility)
      .then(() => {
        document.visible_to_peer = updatedVisibility;
      })
      .catch((error) => {
        this.errorMessage = utils.errorMessage(error);
      });
  }

  public confirmDeleteDocument(document: any) {
    this.deleteDocumentTarget = document;
    this.$bvModal.show("admin-upload-delete-modal");
  }

  public cancelDeleteDocument() {
    this.deleteDocumentTarget = null;
    this.$bvModal.hide("admin-upload-delete-modal");
  }

  public deleteDocument() {
    if (!this.deleteDocumentTarget) {
      return;
    }
    adminUploadsApi
      .deletePeerDocument(this.deleteDocumentTarget.id)
      .then(() => {
        this.$bvModal.hide("admin-upload-delete-modal");
        this.deleteDocumentTarget = null;
        this.fetchAdminDocuments();
      })
      .catch((error) => {
        this.errorMessage = utils.errorMessage(error);
      });
  }

  public previewDocument(document: any) {
    this.previewIsPeer = false;
    this.previewData = [
      {
        type: document.content_type,
        originalFileName: document.original_file_name,
        uploadedFileName: document.file_name,
        fileName: document.file_name,
      },
    ];
    this.showPreviewPopup = true;
  }

  public async downloadDocument(document: any) {
    const fileUrl = `${this.S3Path}/${document.file_name}`;
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const link = window.document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = document.original_file_name || document.file_name;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      this.errorMessage = utils.errorMessage(error);
    }
  }

  public documentExtension(document: any) {
    const name = document.original_file_name || document.file_name || "";
    return name.split(".").pop().toLowerCase();
  }

  public documentIconClass(document: any) {
    const ext = this.documentExtension(document);
    if (this.imageExtensions.includes(ext)) {
      return "doc-icon-img";
    }
    if (ext === "pdf") {
      return "doc-icon-pdf";
    }
    return "doc-icon-doc";
  }

  public formatFileSize(bytes: number) {
    if (!bytes && bytes !== 0) {
      return "--";
    }
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(0)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  public formatDocumentDate(date: string) {
    return date ? moment(date).format("MMM DD, YYYY") : "--";
  }
}
